// Narrow browser transport for the Operator Governance surface. It talks only to same-origin dashboard
// endpoints, sends no Actor/role fields, never retries a mutation, and normalises failures to bounded UI codes.

import type {
  GovernanceDocumentKey,
  GovernanceDocumentView,
  GovernanceListView,
  GovernanceSaveRequest,
  GovernanceSaveResult,
  GovernanceVersionView,
} from './governance-projections.ts';

const GOVERNANCE_PATH = '/dashboard/api/governance';

const documentPath = (documentKey: GovernanceDocumentKey): string =>
  `${GOVERNANCE_PATH}/${encodeURIComponent(documentKey)}`;
const versionPath = (documentKey: GovernanceDocumentKey, versionId: string): string =>
  `${documentPath(documentKey)}/versions/${encodeURIComponent(versionId)}`;
const savePath = (documentKey: GovernanceDocumentKey): string =>
  `${documentPath(documentKey)}/versions`;

export interface GovernanceClientApi {
  readonly getGovernanceDocuments: () => Promise<GovernanceListView>;
  readonly getGovernanceDocument: (
    documentKey: GovernanceDocumentKey,
  ) => Promise<GovernanceDocumentView>;
  readonly getGovernanceVersion: (
    documentKey: GovernanceDocumentKey,
    versionId: string,
  ) => Promise<GovernanceVersionView>;
  readonly saveGovernanceVersion: (
    documentKey: GovernanceDocumentKey,
    request: GovernanceSaveRequest,
  ) => Promise<GovernanceSaveResult>;
}

async function getJson<T>(path: string): Promise<T> {
  const response = await fetch(path, {
    method: 'GET',
    headers: { accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error(`read failed (${String(response.status)})`);
  return (await response.json()) as T;
}

async function safeNote(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as { readonly message?: unknown };
    if (typeof body.message === 'string' && body.message.length > 0) return body.message;
  } catch {
    // Never display an opaque response body; it may contain internal detail.
  }
  return 'The governance document could not be saved.';
}

async function saveVersion(
  documentKey: GovernanceDocumentKey,
  request: GovernanceSaveRequest,
): Promise<GovernanceSaveResult> {
  const response = await fetch(savePath(documentKey), {
    method: 'POST',
    headers: { 'content-type': 'application/json', accept: 'application/json' },
    credentials: 'same-origin',
    // Exact wire contract: no Actor, role, ownership, approval, or version number can be mass-assigned.
    body: JSON.stringify({
      markdown: request.markdown,
      expectedVersionId: request.expectedVersionId,
    }),
  });
  if (response.ok) {
    return { ok: true, document: (await response.json()) as GovernanceDocumentView };
  }
  return {
    ok: false,
    code: response.status === 409 ? 'conflict' : response.status === 400 ? 'invalid' : 'refused',
    note: await safeNote(response),
  };
}

/** The real same-origin Governance transport. */
export function httpGovernanceApi(): GovernanceClientApi {
  return {
    getGovernanceDocuments: () => getJson<GovernanceListView>(GOVERNANCE_PATH),
    getGovernanceDocument: (documentKey) =>
      getJson<GovernanceDocumentView>(documentPath(documentKey)),
    getGovernanceVersion: (documentKey, versionId) =>
      getJson<GovernanceVersionView>(versionPath(documentKey, versionId)),
    saveGovernanceVersion: saveVersion,
  };
}
